import { useAppStore } from "@/global/store";
import { selectUser } from "@/global/store/data/user";
import {
  Box,
  Button,
  Divider,
  Link,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Stack,
  Typography,
} from "@mui/material";
import { AiOutlineDollarCircle } from "react-icons/ai";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";
import { TbShirtSport } from "react-icons/tb";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { createJsxLink } from "@/global/utils/utils.dom";
import { CartManagerState } from "@/global/store/data/purchaseSeats/cartManager";
import Head from "next/head";
import { TeamConfig, useTeamConfig } from "@/global/hooks/useTeamConfig";
import constants from "@/global/config/constants";
import { useRouterExtended } from "@/global/hooks/useRouterExtended";
import { routes } from "@/global/routes";
import NextLink from "next/link";
import { TransferTicketButton } from "@/shared/modules/ticketTransfer/TransferTicketButton";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";
import { useGenericModal } from "@/shared/common/GenericModal";
import { throttleTime } from "@/global/utils/utils";

export const purchaseSeatsLanguages = {
  progressBarSuccessText: "Add more suites or checkout now!",
  bottomBarEventsDescription: "Event",
};

export const purchaseGiftCardLanguages = {
  pageTitle: "Give the gift of Diamondbacks Baseball",
  mainCardTitle: "Share a Gift",
  mainCardDescription:
    "Share the magic of Diamondbacks baseball with a friend or loved one. Upon purchase of the gift, a unique code will be generated for the intended recipient to redeem. Once redeemed, the gift is non-refundable.",
  amountTitle: "Gift Amount",
  recipientTitle: "Recipient",
  recipientEnabledDesc:
    "The Recipient will be sent an email with the unique code for them to redeem their gift. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all Arizona Diamondbacks games at Chase Field.",
  recipientDisabledDesc:
    "If an intended recipient is not specified, once purchased, you will have to forward the unique code to the recipient. Upon your recipient receiving their gift, they will be prompted to enter their unique code to redeem the gift. Once redeemed, the recipient will receive an account credit that they use to purchase tickets for all Arizona Diamondbacks games at Chase Field.",
};

export const redeemGiftCardLanguages = {
  pageTitle: "Give the gift of Diamondbacks Baseball",
  mainCardTitle: "Redeem Gift",
  mainCardLoggedInDesc:
    "Please input the case sensitive code provided to redeem the value of the gift card to the Hippodrome Broadway Series in Baltimore account above.",
  mainCardLoggedOutDesc:
    "Please input the case sensitive unique code provided to redeem the value of the gift card to the Ticketmaster.",
  successDesc: "has been added to to your account!",
};

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "View Available Seats",
    description: "Shop for available seats for your group at Chase Field.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Login to Account Manager",
    description:
      " Click Continue and connect your Arizona Diamondbacks account using our secure Ticketmaster login.",
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for each game.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description:
      "Tickets will be available in your Account Manager within 24 hours of redemption. Reach out to Live Fan Support for any questions.",
  },
];

export const faqs = [
  {
    title: "Are there other seating options available besides what I'm seeing?",
    description: (
      <>
        Yes, currently only a limited number of options are available for
        purchase online. Group options range in size from 15 to over 1,000
        guests are available each game. For information about our other group
        options email{" "}
        {createJsxLink(
          "groupevents@dbacks.com",
          "mailto:groupevents@dbacks.com"
        )}
        , or call 602.462.4600.
      </>
    ),
  },
  {
    title:
      "I have more than 15 guests. Are there group seating options still available for me?",
    description: (
      <>
        Yes, there are a variety of other group options at Chase Field beyond
        those listed, ranging from 15 guests to over 1,000. For additional
        information email{" "}
        {createJsxLink(
          "groupevents@dbacks.com",
          "mailto:groupevents@dbacks.com"
        )}
        , or call 602.462.4600.
      </>
    ),
  },
  {
    title:
      "The game I want to attend isn't show here, are group tickets still available for it?",
    description: (
      <>
        Yes, there are group options available for all games at Chase Field
        beyond those listed. For additional information email{" "}
        {createJsxLink(
          "groupevents@dbacks.com",
          "mailto:groupevents@dbacks.com"
        )}
        , or call 602.462.4600.
      </>
    ),
  },
  {
    title: "Are there options with food/beverage available?",
    description: (
      <>
        Yes, we have D-bucks available to add on to any group purchase. D-bucks
        can be loaded in increments of $5 and can be used to purchase food at
        any of our D-backs concession stands. We also have an all-you-can-eat
        section that includes an all-inclusive menu of hotdogs, chips, popcorn,
        peanuts & Pepsi products. For additional information email{" "}
        {createJsxLink(
          "groupevents@dbacks.com",
          "mailto:groupevents@dbacks.com"
        )}
        , or call 602.462.4600.
      </>
    ),
  },
  {
    title: "Can I speak with someone about the best available seating options?",
    description: (
      <>
        To speak to a D-backs group sales representative, please call D-backs
        Group Sales line at 602.462.4600.
      </>
    ),
  },
  {
    title: "What happens after I purchase group tickets online?",
    description: (
      <>
        Following your purchase, a D-backs representative will follow up with
        you to confirm your order and assist with any questions you have in
        anticipation of your event. Tickets will be added to your D-backs
        account where you’ll be able to access them via the MLB Ballpark app or
        in your D-backs account manager by visiting{" "}
        {createJsxLink(
          "www.dbacks.com/myaccount",
          "https://www.dbacks.com/myaccount"
        )}
        . All group tickets are digital.
      </>
    ),
  },
  {
    title: "Are there taxes/fees?",
    description:
      "There are no additional taxes or fees on the pricing you see listed.",
  },
  {
    title: "Have questions about group tickets or hospitality spaces?",
    description: (
      <>
        For additional information, email{" "}
        {createJsxLink(
          "groupevents@dbacks.com",
          "mailto:groupevents@dbacks.com"
        )}
        , or call 602.462.4600.
      </>
    ),
  },
];

export function TermsAndCondition() {
  return (
    <>
      By purchasing you agree to receive SMS alerts regarding ticket delivery
      and agree to the{" "}
      <Link
        // fontWeight={"bold"}
        // textDecor="underline"

        href={
          "https://uploads-ssl.webflow.com/5ed7df6fe67e862e0a62f9f2/616882d2b691e09f75348396_Terms%20%26%20Conditions.pdf"
        }
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        Terms & Conditions
      </Link>{" "}
      and{" "}
      <Link
        // fontWeight={"bold"}
        // textDecor="underline"
        href={
          "https://uploads-ssl.webflow.com/5ed7df6fe67e862e0a62f9f2/63c05d80816dac05b5b34ac7_seasonshare%20CCPA%20Privacy%20Policy%20Agreement.pdf"
        }
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        Privacy Policy
      </Link>
      .
    </>
  );
}

export function createPerkTotalLabel(cartManager: CartManagerState) {
  return "Add On Balance";
}

export const AnalyticsHeader = (props: { teamConfig: TeamConfig }) => {
  const { teamSchema } = props.teamConfig;

  return (
    <Head>
      <script
        nonce={process.env.pixelScriptNonce}
        id={`${teamSchema}-meta-pixel-script`}
        defer
        dangerouslySetInnerHTML={{
          __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1532627770340759');
              fbq('track', 'PageView');
              console.log('meta pixel page load event fired.');
              window.addEventListener("${constants.customEvents.eventPurchaseSuccess}", function(e) {
                try {
                  const { cartRes, checkoutRes } = e.detail;
                  fbq('track', 'Purchase', {
                    content_name: "${teamSchema}_event_purchase", 
                    value: String(cartRes.allInPrice), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                } catch (error) {
                  console.log('Error in meta pixel purchase event', error); 
                }
              });

              window.addEventListener("${constants.customEvents.giftPurchaseSuccess}", function(e) {
                try {
                  const { checkoutRes } = e.detail;
                  fbq('track', 'Purchase', {
                    content_name: "${teamSchema}_gift_purchase", 
                    value: String(checkoutRes.amount), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                } catch (error) {
                  console.log('Error in meta pixel purchase event', error); 
                }
              });
            `,
        }}
      />

      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=1532627770340759&ev=PageView&noscript=1"
        />
      </noscript>
    </Head>
  );
};

export function CheckoutSuccessDescription() {
  const teamConfig = useTeamConfig();
  const { md: mdUp } = useCssMediaQuery("up");
  const router = useRouterExtended();
  const viewTicketsModal = useGenericModal();

  const onClickViewTicket = () => {
    viewTicketsModal.set({
      isOpen: true,
      title: <Box height="32px"></Box>,
      hideTitleIcon: true,
      description: (
        <Box>
          <Typography variant="body1" sx={{ mb: 3 }}>
            By clicking View Tickets, you will be redirected to the D-backs
            Account Manager to access your barcodes. Please note that any ticket
            transfers or forwarding completed there will not be reflected in
            this portal. For a seamless experience, please manage all transfers
            and forwarding through the Transfer Tickets portal.
          </Typography>
        </Box>
      ),

      closeIconProps: {
        onClick: () => {
          viewTicketsModal.set({ isOpen: false });
          throttleTime(400, () => {
            viewTicketsModal.reset();
          });
        },
      },

      closeButtonText: "View Tickets",
      closeButtonProps: {
        variant: "outlined",
      },
      onClickClose: () => {
        window.location.href = teamConfig.accountManagerUrl;
      },

      confirmButtonText: "Transfer Tickets",
      onClickConfirm: async () => {
        await router.push({
          pathname: routes.ticket_transfer,
          query: {
            ...router.query,
            eventCode: router?.query?.eventCode as string,
            orderNumber: Number(router?.query?.orderNumber),
          },
        });
      },
    });
  };

  return (
    <Box>
      <Typography fontSize={"1.1rem"} color={"text.primary"} lineHeight={1.4}>
        Your order was successful! If you&apos;d like to transfer tickets to
        members of your group. Click Transfer Tickets to get started.
      </Typography>
      <Stack
        direction={mdUp ? "row" : "column"}
        justifyContent={"center"}
        alignItems="center"
        spacing={2}
        mt={2.5}
      >
        <TransferTicketButton
          size="large"
          eventCode={router?.query?.eventCode as string}
          orderNumber={Number(router?.query?.orderNumber)}
          sx={{
            width: mdUp ? "inherit" : "100%",
          }}
        />

        <ModalActionButton
          variant="outlined"
          onClick={onClickViewTicket}
          size="large"
          sx={{
            width: mdUp ? "170px" : "100%",
          }}
        >
          View Tickets
        </ModalActionButton>
      </Stack>
    </Box>
  );
}
